/** Hover/focus hint for disabled controls — avoids floating-ui (disabled buttons swallow events). */

import { useEffect, useId, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";

interface DisabledActionTooltipProps {
  reasons: string[];
  /** Heading above the list when there is more than one reason. */
  heading?: string;
  children: ReactElement;
}

function ReasonsContent({
  reasons,
  heading,
}: {
  reasons: string[];
  heading?: string;
}): ReactNode {
  if (reasons.length === 1 && !heading) {
    return <p className="text-sm leading-snug">{reasons[0]}</p>;
  }

  return (
    <div className="space-y-1.5" data-testid="disabled-action-reasons">
      {heading ? <p className="text-sm font-medium text-white">{heading}</p> : null}
      <ul className="list-disc space-y-1 pl-4 text-sm leading-snug text-slate-200">
        {reasons.map((reason, index) => (
          <li key={`${index}-${reason}`}>{reason}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Disabled buttons do not receive pointer events, so the hover target is a
 * wrapping span. When there are no reasons, children render unchanged.
 *
 * Renders at most one portaled popover (no native `title`) so hints never stack.
 */
export function DisabledActionTooltip({
  reasons,
  heading = "Can't continue yet",
  children,
}: DisabledActionTooltipProps) {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open || !anchor) {
      setCoords(null);
      return;
    }

    const update = () => {
      const rect = anchor.getBoundingClientRect();
      setCoords({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    };

    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, anchor]);

  if (reasons.length === 0) return children;

  const multi = reasons.length > 1;
  const summary = reasons.join(". ");

  return (
    <span
      ref={setAnchor}
      className="relative inline-flex"
      tabIndex={0}
      aria-label={summary}
      aria-describedby={open ? tooltipId : undefined}
      data-testid="disabled-action-tooltip"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open &&
        coords &&
        createPortal(
          <span
            id={tooltipId}
            role="tooltip"
            className={clsx(
              "pointer-events-none fixed z-[99999] w-max max-w-[18rem] -translate-x-1/2 -translate-y-full",
              "rounded-2xl bg-slate-900 px-4 py-3 text-white shadow-2xl dark:bg-slate-800 dark:text-slate-200",
            )}
            style={{ top: coords.top, left: coords.left }}
          >
            <ReasonsContent reasons={reasons} heading={multi ? heading : undefined} />
          </span>,
          document.body,
        )}
    </span>
  );
}
